import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ShieldAlert, AlertTriangle, Clock, PawPrint, Lightbulb, Check, Pencil, Trash2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import LanguageToggle from '../components/LanguageToggle';
import BottomNavigation from '../components/BottomNavigation';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import useAuthorityProfile from '../hooks/useAuthorityProfile';
import { useAuth, API } from '../App';

const EDIT_CATEGORIES = ['safety_tip', 'incident_factual', 'lost_pet', 'community_alert'];

const STATUS_BADGES = {
  pending: 'bg-amber-50 text-amber-800 ring-amber-200',
  approved: 'bg-emerald-50 text-emerald-800 ring-emerald-200',
  rejected: 'bg-red-50 text-red-800 ring-red-200',
  expired: 'bg-gray-100 text-gray-700 ring-gray-200',
  takedown: 'bg-red-50 text-red-800 ring-red-200',
  withdrawn: 'bg-gray-100 text-gray-500 ring-gray-200',
};
const CATEGORY_ICONS = {
  safety_tip: Lightbulb,
  incident_factual: AlertTriangle,
  lost_pet: PawPrint,
  community_alert: Clock,
};
const CATEGORY_BADGES = {
  safety_tip: 'bg-orange-50 text-orange-800 ring-orange-200',
  incident_factual: 'bg-red-50 text-red-800 ring-red-200',
  lost_pet: 'bg-emerald-50 text-emerald-800 ring-emerald-200',
  community_alert: 'bg-purple-50 text-purple-800 ring-purple-200',
};
const CATEGORY_ACCENTS = {
  safety_tip: 'border-l-4 border-l-orange-400',
  incident_factual: 'border-l-4 border-l-red-500',
  lost_pet: 'border-l-4 border-l-emerald-500',
  community_alert: 'border-l-4 border-l-purple-500',
};

const SafetyBulletinMine = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const authority = useAuthorityProfile();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [towns, setTowns] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: '', body: '', category: '', town: '' });
  const [notifyFollowers, setNotifyFollowers] = useState(false);
  const [saving, setSaving] = useState(false);
  const [withdrawing, setWithdrawing] = useState(null);
  const [withdrawBusy, setWithdrawBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/safety-bulletin/towns`).then((r) => setTowns(r.data?.towns || [])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/safety-bulletin/mine`, {
        headers: { Authorization: `Bearer ${user.token}` },
      });
      setItems(r.data.bulletins || []);
    } finally {
      setLoading(false);
    }
  }, [user]);

  const markFound = async (id) => {
    try {
      await axios.post(
        `${API}/safety-bulletin/${id}/mark-found`,
        {},
        { headers: { Authorization: `Bearer ${user.token}` } },
      );
      toast.success(t('safety.mine.markFoundSuccess', 'Marked as found — thanks for letting us know!'));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('safety.mine.markFoundFailed', 'Could not mark as found.'));
    }
  };

  const openEdit = (b) => {
    setForm({ title: b.title || '', body: b.body || '', category: b.category || '', town: b.town || '' });
    setNotifyFollowers(false);
    setEditing(b);
  };

  const withdraw = async () => {
    if (!withdrawing) return;
    setWithdrawBusy(true);
    try {
      await axios.post(
        `${API}/safety-bulletin/${withdrawing.id}/withdraw`,
        {},
        { headers: { Authorization: `Bearer ${user.token}` } },
      );
      toast.success(t('safety.mine.withdrawSuccess', 'Alert withdrawn — it is no longer visible to the public.'));
      setWithdrawing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('safety.mine.withdrawFailed', 'Could not withdraw the alert.'));
    } finally {
      setWithdrawBusy(false);
    }
  };

  const saveEdit = async () => {
    if (!editing) return;
    // Re-notify is only meaningful for official authors editing a LIVE alert
    // (community edits revert to pending). Mirror the backend guard here.
    const canNotify = authority.isOfficial && editing.status === 'approved';
    setSaving(true);
    try {
      const r = await axios.put(
        `${API}/safety-bulletin/${editing.id}`,
        { title: form.title, body: form.body, category: form.category, town: form.town, notifyFollowers: canNotify && notifyFollowers },
        { headers: { Authorization: `Bearer ${user.token}` } },
      );
      toast.success(
        r.data?.notified
          ? t('safety.mine.editSuccessNotified', 'Saved — your followers have been notified of the update.')
          : r.data?.revertedToPending
            ? t('safety.mine.editSuccessPending', 'Saved — your alert is back in review and will go live once approved.')
            : t('safety.mine.editSuccessLive', 'Your alert has been updated.'),
      );
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('safety.mine.editFailed', 'Could not save changes. Please try again.'));
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (!user) { navigate('/signin'); return; }
    load();
  }, [user, load, navigate]);

  return (
    <div className="min-h-screen bg-gray-50 pb-24" data-testid="safety-mine-page">
      <header className="bg-slate-800 text-white shadow-lg">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <button onClick={() => navigate('/safety-bulletin')} className="p-2 rounded hover:bg-white/10" data-testid="safety-mine-back-btn">
            <ArrowLeft className="h-5 w-5 rtl-flip" />
          </button>
          <h1 className="text-base font-semibold">{t('safety.myPosts')}</h1>
          <LanguageToggle />
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6">
        {loading ? (
          <div className="text-center text-gray-500 py-12">…</div>
        ) : items.length === 0 ? (
          <div className="bg-white rounded-xl p-8 text-center border border-gray-200" data-testid="safety-mine-empty">
            <ShieldAlert className="h-10 w-10 text-gray-300 mx-auto mb-2" />
            <p className="text-gray-500">{t('safety.mine.empty')}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((b) => {
              const badge = STATUS_BADGES[b.status] || 'bg-gray-50 text-gray-700 ring-gray-200';
              const catBadge = CATEGORY_BADGES[b.category] || 'bg-gray-50 text-gray-700 ring-gray-200';
              const CatIcon = CATEGORY_ICONS[b.category] || ShieldAlert;
              const accent = CATEGORY_ACCENTS[b.category] || '';
              return (
                <article
                  key={b.id}
                  className={`bg-white rounded-xl border border-gray-200 p-4 shadow-sm ${accent}`}
                  data-testid={`safety-mine-item-${b.id}`}
                >
                  <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ring-1 ${catBadge}`}>
                        <CatIcon className="h-3.5 w-3.5" />
                        {t(`safety.category.${b.category}`)}
                      </span>
                      <span className="text-xs text-gray-500">{b.town}</span>
                    </div>
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ring-1 ${badge}`}>
                      {t(`safety.mine.status.${b.status}`)}
                    </span>
                  </div>
                  <h2 className="text-base font-semibold text-gray-900 mb-1">{b.title}</h2>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{b.body}</p>
                  {b.status === 'rejected' && b.rejectionReason && (
                    <p className="mt-3 text-sm text-red-700 bg-red-50 border-l-4 border-red-300 pl-3 py-2 rounded">
                      {t('safety.mine.rejectionReason', { reason: b.rejectionReason })}
                    </p>
                  )}

                  {/* Edit — author can edit their own pending or live alerts.
                      Editing a live community alert sends it back to review. */}
                  {(b.status === 'pending' || b.status === 'approved') && (
                    <Button
                      onClick={() => openEdit(b)}
                      size="sm"
                      variant="outline"
                      className="mt-3 mr-2"
                      data-testid={`safety-mine-edit-${b.id}`}
                    >
                      <Pencil className="h-4 w-4 mr-1" />
                      {t('safety.mine.edit', 'Edit')}
                    </Button>
                  )}

                  {/* Withdraw — soft-deletes the alert from all public surfaces
                      and retracts every linked notification. Kept in history. */}
                  {(b.status === 'pending' || b.status === 'approved') && (
                    <Button
                      onClick={() => setWithdrawing(b)}
                      size="sm"
                      variant="outline"
                      className="mt-3 mr-2 text-red-600 border-red-200 hover:bg-red-50"
                      data-testid={`safety-mine-withdraw-${b.id}`}
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      {t('safety.mine.withdraw', 'Withdraw')}
                    </Button>
                  )}

                  {/* "Mark as Found" — only for live Lost Pet posts the user owns */}
                  {b.category === 'lost_pet' && b.status === 'approved' && (
                    <Button
                      onClick={() => markFound(b.id)}
                      size="sm"
                      className="mt-3 bg-emerald-600 hover:bg-emerald-700 text-white"
                      data-testid={`safety-mine-mark-found-${b.id}`}
                    >
                      <Check className="h-4 w-4 mr-1" />
                      {t('safety.mine.markFound', 'Mark as Found')}
                    </Button>
                  )}

                  {b.closedReason === 'pet_found' && (
                    <p
                      className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1"
                      data-testid={`safety-mine-closed-found-${b.id}`}
                    >
                      <Check className="h-3.5 w-3.5" />
                      {t('safety.mine.closedFound', 'Closed — pet found')}
                    </p>
                  )}
                </article>
              );
            })}
          </div>
        )}
      </main>

      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) setEditing(null); }}>
        <DialogContent data-testid="safety-mine-edit-dialog">
          <DialogHeader>
            <DialogTitle>{t('safety.mine.editHeading', 'Edit alert')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium text-gray-700">{t('safety.mine.editTown', 'Town')}</label>
              <select
                value={form.town}
                onChange={(e) => setForm((f) => ({ ...f, town: e.target.value }))}
                className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white"
                data-testid="safety-mine-edit-town"
              >
                {towns.map((tn) => <option key={tn} value={tn}>{tn}</option>)}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">{t('safety.mine.editCategory', 'Category')}</label>
              <select
                value={form.category}
                onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white"
                data-testid="safety-mine-edit-category"
              >
                {EDIT_CATEGORIES.map((c) => <option key={c} value={c}>{t(`safety.category.${c}`)}</option>)}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">{t('safety.mine.editTitleField', 'Title')}</label>
              <Input
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                className="mt-1"
                data-testid="safety-mine-edit-title"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">{t('safety.mine.editBody', 'Details')}</label>
              <Textarea
                value={form.body}
                onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
                rows={5}
                className="mt-1"
                data-testid="safety-mine-edit-body"
              />
            </div>
            {authority.isOfficial && editing?.status === 'approved' ? (
              <>
                <p className="text-xs text-gray-500" data-testid="safety-mine-edit-live-note">
                  {t('safety.mine.editLiveNote', 'Your edit stays live immediately — followers are not notified unless you choose to below.')}
                </p>
                <label className="flex items-start gap-2 cursor-pointer rounded-md bg-amber-50 ring-1 ring-amber-200 px-3 py-2" data-testid="safety-mine-edit-notify-label">
                  <Checkbox
                    checked={notifyFollowers}
                    onCheckedChange={(v) => setNotifyFollowers(!!v)}
                    className="mt-0.5"
                    data-testid="safety-mine-edit-notify-followers"
                  />
                  <span className="text-sm text-amber-900">
                    {t('safety.mine.editNotifyFollowers', 'Notify followers of this update')}
                  </span>
                </label>
              </>
            ) : (
              <p className="text-xs text-gray-500">{t('safety.mine.editPendingNote', 'Editing a published alert sends it back for review before it goes live again.')}</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} disabled={saving} data-testid="safety-mine-edit-cancel">
              {t('safety.mine.editCancel', 'Cancel')}
            </Button>
            <Button onClick={saveEdit} disabled={saving} className="bg-slate-800 hover:bg-slate-900 text-white" data-testid="safety-mine-edit-save">
              {saving ? t('common.loading', 'Loading…') : t('safety.mine.editSave', 'Save changes')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!withdrawing} onOpenChange={(o) => { if (!o) setWithdrawing(null); }}>
        <DialogContent data-testid="safety-mine-withdraw-dialog">
          <DialogHeader>
            <DialogTitle>{t('safety.mine.withdrawHeading', 'Withdraw alert')}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600">
            {t('safety.mine.withdrawNote', 'This removes your alert from all public feeds and clears related notifications for everyone. It stays in your history as Withdrawn.')}
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWithdrawing(null)} disabled={withdrawBusy} data-testid="safety-mine-withdraw-cancel">
              {t('safety.mine.editCancel', 'Cancel')}
            </Button>
            <Button onClick={withdraw} disabled={withdrawBusy} className="bg-red-600 hover:bg-red-700 text-white" data-testid="safety-mine-withdraw-confirm">
              {withdrawBusy ? t('common.loading', 'Loading…') : t('safety.mine.withdrawConfirm', 'Withdraw alert')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <BottomNavigation />
    </div>
  );
};

export default SafetyBulletinMine;
